import {
  useContext, useState, useMemo, useEffect, useCallback, useRef,
} from 'react';
import { useTheme } from 'styled-components';

// contexts
import { AlertContext } from '../../context/AlertProvider';
import { ModalContext } from '../../context/ModalProvider';
import { FilesContext } from '../../context/FilesProvider';
import { AuthContext } from '../../context/AuthProvider';
import { UsersContext } from '../../context/UsersProvider';
import { GroupsContext } from '../../context/GroupsProvider';
import { TourContext } from '../../context/TourProvider';
import { TemplateContext } from '../../context/TemplateProvider';
import { NotificationsContext } from '../../context/NotificationProvider';
// hooks
import useGridDashboard from '../../hooks/useGridDashboard';
import useGridKpi from '../../hooks/useGridKpi';

// functions / utils
// import { getRefDashboardItem } from '../../utils/firestore';
// import {
//   getFormatFromMetricsFile, toMb,
// } from '../../utils/functions/verifyFiles';
import { shareDashOptions } from '../../options';

// components
import Loader from '../../juristec-ui/core/Loader';
import GridDashboardToolbar from '../../components/Toolbars/GridDashboardToolbar';
import ShareList, { PublicLinkAlert } from '../../components/Modals/ShareList';
import NewDashboard from '../../components/Modals/NewDashboard';
import DashboardCore from '../../components/DashboardCore';
import ConfirmCopyToMe from '../../components/Modals/ConfirmCopyToMe';
// import OverrideFilesName from '../../components/Modals/OverrideFilesName';
import CopyDashboard from '../../components/ProgressiveModals/CopyDashboard';
import ImportDashboard from '../../components/Modals/ImportDashboard';

import { verifyFileImgType, verifyFileSize } from '../../juristec-ui/utils/validators/fileValidators';
import urlHash from '../../juristec-ui/utils/functions/urlHash';
import DashboardsLinks from '../../components/Modals/DashboardsLinks/DashboardsLinks';
import DashboardInfo from '../../components/Modals/DashboardInfo/DashboardInfo';

export default function DashboardPage() {
  const {
    currentUser, userCompany, user, plans,
  } = useContext(AuthContext);
  const { setAlertConfig } = useContext(AlertContext);
  const { setModalConfig, toggleModal, closeModal } = useContext(ModalContext);
  const { state: groupsState, groupsAPI } = useContext(GroupsContext);
  const { state: usersState } = useContext(UsersContext);
  const { templateAPI } = useContext(TemplateContext);
  const { state: sysMsgState, notificationsAPI } = useContext(NotificationsContext);
  const {
    tourOpen, nextStep, refreshTour, remakeTourList,
  } = useContext(TourContext);
  // const { setModalConfig, closeModal } = useContext(ModalContext);

  const { state: filesState, filesAPI } = useContext(FilesContext);
  const theme = useTheme();

  const isMounted = useRef(false);

  useEffect(() => {
    if (!filesState.started && !filesState.isLoading && !isMounted.current) {
      (async () => {
        await filesAPI.init();
      })();
    }

    if (!groupsState.started && !groupsState.isLoading) {
      (async () => {
        await groupsAPI.getGroups();
      })();
    }
  }, [filesState.started, filesAPI, filesState.isLoading, groupsState.started, groupsAPI]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [{
    isLoading,
    dashboards,
    sharedDashboards,
    dashboardLayout,
    groups,
  }, gridAPI] = useGridDashboard(currentUser, user);
  const [gridKpiState, gridKpiAPI] = useGridKpi(currentUser.id, '', currentUser, user);

  const [canEditLayout, setEditLayout] = useState(null);
  const [_currentBreakpoint, _setCurrentBreakPoint] = useState('lg');
  const [_layout, _setLayout] = useState({
    xxs: [],
    xs: [],
    sm: [],
    md: [],
    lg: [],
  });

  // filters vars
  const [filterText, setFilterText] = useState('');
  const [selectedGeneral, setSelectedGeneral] = useState('all');
  const [selectedGroups, setSelectedGroups] = useState({ });
  const [filterAgain, setFilterAgain] = useState(false);

  const [openTrashCan, setOpenTrashCan] = useState(false);
  const [countTrashCan, setCountTrashCan] = useState(0);

  const [multiSelection, setMultiSelection] = useState(false);
  const [selectedCards, setSelectedDashs] = useState(new Map());

  useEffect(() => {
    if (dashboardLayout) _setLayout(dashboardLayout);
  }, [dashboardLayout]);

  const filterList = useCallback((listIn, searchTextIn) => {
    let countTrash = 0;
    const filtered = listIn.filter((dash) => {
      if (openTrashCan) {
        if (dash.isDeleted && dash.owner === user.id) {
          countTrash += 1;
          return true;
        } return false;
      }
      if (dash.isDeleted) {
        if (dash.owner === user.id) countTrash += 1;
        return false;
      }

      switch (selectedGeneral) {
        case 'shared':
          if (!dash.shared) return false;
          break;
        case 'my':
          if (dash.shared) return false;
          break;
        case 'bookmarked':
          if (!dash.bookMarked) return false;
          break;
        default:
          break;
      }
      // console.log('if ', Object.keys(selectedGroups));
      if (Object.keys(selectedGroups).length) {
        // console.log({ v: dash.group.value, teste: selectedGroups[dash.group.value] });
        if (!selectedGroups[dash.group?.value || '']) return false;
      }

      if (!searchTextIn) return true;

      const search = searchTextIn.toLowerCase().trim();
      return (
        dash.name.toLowerCase().includes(search)
        || dash.displayName.toLowerCase().includes(search)
        || dash.group?.label?.toLowerCase().includes(search)
      );
    });
    setCountTrashCan(countTrash);
    return filtered;
  }, [filterAgain]);

  // testar isso. dps corrigir css. dps arrumar o dashboradGrid nomes das var
  const dashWithUser = useMemo(() => {
    const { users } = usersState;
    const allDashs = dashboards.concat(sharedDashboards);
    return allDashs.map((d) => {
      const owner = users.reduce((acc, cur) => {
        let aux = acc;
        if (cur.id === d.owner || cur.uid === d.owner) aux = cur;
        return aux;
      }, {});

      return {
        ...owner,
        ownerName: owner.name,
        ...d,
      };
    });
  }, [dashboards, sharedDashboards, usersState]);

  const filteredDash = useMemo(() => filterList(dashWithUser, filterText),
    [filterText, dashWithUser, filterAgain]);

  // handlers
  const saveLayout = async () => {
    const result = await gridAPI.saveLayout(_layout);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
    }
    setEditLayout(false);
  };

  const handleGeneral = (a) => {
    setFilterAgain((p) => !p);
    return setSelectedGeneral(a || 'all');
  };

  const handleGroup = (a) => {
    setFilterAgain((p) => !p);
    return setSelectedGroups(a);
  };

  const handleTrashCan = (a) => {
    setFilterAgain((p) => !p);
    return setOpenTrashCan(a);
  };

  const getDashInfos = async () => {
    const res = await gridKpiAPI.loadCardsFromCollection();
    return res;
  };

  const verifyMemoryAndDashboards = () => {
    if (filesState.size + 10 > userCompany?.volumeData) {
      setAlertConfig({
        type: 'error',
        text: 'Não há memória suficiente!',
        child: 'A sua instância já atingiu o limite máximo de memória disponível!',
      });
      return false;
    }
    if (filesState.dashboards >= userCompany?.dashboards) {
      setAlertConfig({
        type: 'error',
        text: 'Número máximo de dashboards atingido!',
        child: `O número máximo de dashboards do seu plano (${userCompany?.dashboards} dashboards) foi atingido!`,
      });
      return false;
    }
    return true;
  };
  // o tratamento de erro  tá no newDashboard.jsx
  const handleCreateModal = async () => {
    if (!verifyMemoryAndDashboards()) return;
    if (tourOpen) nextStep();

    const verifyAdd = async (newDash) => {
      const result = await gridAPI.createDashboard(newDash, dashboardLayout);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          // text: result.msg,
          text: 'Tivemos um problema para criar o seu dashboard. Tente novamente, por favor!',
          child: `${result.raw}`,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: 'O dashboard foi criado com sucesso!',
      });
      closeModal();
      await filesAPI.getCurrentCompanyStorage();
    };

    // Function to check if the image satisfies the specified conditions
    const checkFileIsImg = (file) => {
      const errorMsg = verifyFileImgType(file) || verifyFileSize(file, 1000000);
      if (errorMsg.length > 0) {
        setAlertConfig({ type: 'error', text: errorMsg });
        return false;
      }
      return true;
    };

    setModalConfig({
      title: 'Novo Dashboard',
      className: 'newDash_modal',
      children: (
        <NewDashboard
          tourContext={{
            tourOpen, nextStep, remakeTourList, refreshTour,
          }}
          close={closeModal}
          createNewDashboard={verifyAdd}
          groupNameList={Object.keys(groups).map((g) => ({ label: g, value: g, id: g }))}
          currentUser={user}
          checkImage={checkFileIsImg}
        />
      ),
    });
  };

  const handleEditModal = (dashboard) => {
    const verifyEdit = async (dashId, sharedWith, newInfos) => {
      const result = await gridAPI.editDashboard(dashId, sharedWith, newInfos);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      closeModal();
    };

    // Function to check if the image satisfies the specified conditions
    const checkFileIsImg = (file) => {
      const errorMsg = verifyFileImgType(file) || verifyFileSize(file, 1000000);
      if (errorMsg.length > 0) {
        setAlertConfig({ type: 'error', text: errorMsg });
        return false;
      }
      return true;
    };

    setModalConfig({
      title: 'Editar Dashboard',
      children: (
        <NewDashboard
          toEditDashboard={dashboard}
          close={closeModal}
          currentUser={user}
          editDashboard={verifyEdit}
          groupNameList={Object.keys(groups).map((g) => ({ label: g, value: g, id: g }))}
          checkImage={checkFileIsImg}
          isEdit
        />
      ),
    });
  };
  //

  const handleShareDash = (dash) => {
    const companyPlan = plans[userCompany.plan];
    if (tourOpen) nextStep();
    const genSuccessMsg = (sharedItems = [], missing) => {
      let child = '';
      let type = 'success';
      if (sharedItems.length > 0) {
        child = `O dashboard ${dash.name} está compartilhado com ${sharedItems.map((u) => (
          `${u.name} (${shareDashOptions.get(u.sharePermission?.value)})`)).join(', ')}.`;
        if (missing?.deleted?.length > 0 || missing?.noPermission?.length > 0) {
          type = 'warning';
          child += " Porém alguns KPI's dependem de";
          if (missing.deleted.length > 0) {
            child += ` ${missing.deleted.length} arquivos não encontrados${missing.noPermission.length > 0 ? ' e' : '.'}`;
          }
          if (missing.noPermission.length > 0) child += ` ${missing.deleted.length} arquivos que não são seus.`;
        }
        child = (
          <>
            <div>
              {child}
            </div>
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: theme.warning, fontWeight: 'bold' }}>
                Atenção:
              </span>
              {' '}
              <span>
                Os arquivos dependentes utilizados no dashboard também serão compartilhados
                para os usuários cientistas e proprietários selecionados,
                com permissão de Visualização.
              </span>
            </div>
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: theme.warning, fontWeight: 'bold' }}>
                Atenção 2:
              </span>
              {' '}
              <span>
                Filtros globais e variáveis de controle podem não estar disponíveis para os usuários
                compartilhados caso você não seja o proprietário dos arquivos dependentes.
              </span>
            </div>
          </>
        );
      } else {
        child = `O dashboard ${dash.name} não está compartilhado com outros usuários.`;
      }
      return {
        text: 'Opções de compartilhamento atualizadas!',
        type,
        child,
      };
    };

    const verifyShare = async (selectedUsers, selectedGroups, removed) => {
      const result = await gridAPI.shareDashboard(
        dash, usersState.users, selectedUsers, selectedGroups, removed, filesState.files,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }
      setAlertConfig(genSuccessMsg([...selectedUsers, ...selectedGroups], result.missing));
      closeModal();
    };

    const genPublicUrl = async (operation, publicUrl, pwd, expDt) => {
      const res = await gridAPI.genPublicKpis(
        { ...dash, publicUrl: publicUrl ?? dash.publicUrl },
        operation,
        pwd,
        expDt,
      );
      const isDel = operation === 'delete';
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: `Não foi possível ${isDel ? 'desativar o link público' : 'gerar cópia pública'}. Tente novamente mais tarde`,
        });
      } else {
        setAlertConfig({
          type: 'success',
          text: `Link público ${isDel ? 'desativado' : 'atualizado'} com sucesso.`,
          child: !isDel ? <PublicLinkAlert /> : null,
        });
      }
      return res;
    };
    setModalConfig({
      title: 'Compartilhar Dashboard',
      className: 'shareModal',
      children: (
        <ShareList
          users={usersState.users}
          groups={groupsState.groups}
          owner={user.id}
          publicLink={userCompany.publicLink}
          sharedWith={dash?.sharedWith || {}}
          submit={verifyShare}
          close={closeModal}
          publicUrl={dash.publicUrl}
          publicTimestamp={dash.publicTimestamp}
          genPublicUrl={genPublicUrl}
          tourContext={{ tourOpen, nextStep, refreshTour }}
          permissionsOpts={shareDashOptions.getOptions()}
          expiresAt={dash.publicExpiration}
          passwordProtected={dash?.passwordProtected}
          getAllInfos={getDashInfos}
          permissionsPlan={{
            daysToExpire: (
              userCompany.publicLinkExpirationDays ?? companyPlan.publicLinkExpirationDays
            ),
            password: userCompany.publicLinkPwd ?? companyPlan.publicLinkPwd,
          }}
        />
      ),
    });
  };

  const handleSelfRemove = (dash) => {
    const selfRemoval = async () => {
      const result = await gridAPI.selfRemoval(dash);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: result.raw,
        });
      } else {
        setAlertConfig({
          type: 'success',
          text: 'Você foi removido do compartilhamento!',
        });
      }
      filesAPI.init();
    };

    setAlertConfig({
      type: 'warning',
      text: 'Tem certeza que deseja sair do compartilhamento?',
      child: (
        <span>
          Você não poderá mais visualizar o dashboard
          {' '}
          <strong style={{ fontStyle: 'italic' }}>
            {dash.name}
          </strong>
        </span>
      ),
      withoutConfirm: true, // tirar o ok do alerta
      withFunction: true, // colocar btn cancelar e confirmar
      confirmFunction: selfRemoval,
    });
  };

  const handleBookMarkDash = async (dash) => {
    const result = await gridAPI.bookMarkDashboard(dash.id, dash.bookMarked, dash.shared);
    if (result.error) {
      setAlertConfig({
        text: result.msg,
        type: 'error',
      });
    }
  };

  /* ========================================================= */

  const handleCopyDashboard = async (dash) => {
    if (!verifyMemoryAndDashboards()) return;
    const handle = async () => {
      gridAPI.setLoading(true);
      const dashId = dash.id.split('_')[1];

      const result = await gridAPI.cloneDashboard(dashId, dash.owner);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: result.raw,
        });
        closeModal();
        return;
      }
      if (result.databases?.length) {
        let deleted = 0;
        const notAllowed = [];
        result.databases.forEach((db) => {
          const used = filesState.instanceFiles.find((f) => f.file_id === db);
          if (!used) {
            deleted += 1;
          } else {
            const shared = filesState.files.find((f) => f.file_id === db);
            if (!shared) {
              notAllowed.push({
                ...used,
                ownerName: usersState.users.find((u) => u.id === used.owner)?.name,
              });
            }
          }
        });
        if (deleted > 0 || notAllowed.length > 0) {
          setAlertConfig({
            type: 'warning',
            text: 'Dashboard clonado com sucesso!',
            child: (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                  <b style={{ color: theme.error }}>Atenção: </b>
                  você possui
                </span>
                {notAllowed.length > 0 && (
                  <>
                    <span>
                      <b>
                        {notAllowed.length}
                      </b>
                      {' '}
                      {notAllowed.length > 1 ? 'arquivos ' : 'arquivo '}
                      sem permissão de uso:
                    </span>
                    <ul style={{ margin: '5px 0' }}>
                      {notAllowed.map((na) => (
                        <li key={na.file_id}>
                          {'Arquivo: '}
                          <b>
                            {na.filename}
                          </b>
                          {' - proprietário: '}
                          <b>
                            {na.ownerName}
                          </b>
                        </li>
                      ))}
                    </ul>
                    <span>
                      Para que estes arquivos sejam atualizados, solicite o compartilhamento
                      {' '}
                      {notAllowed.length > 1 ? 'dos arquivos.' : 'do arquivo.'}
                    </span>
                  </>
                )}
                {deleted > 0 && (
                  <span>
                    <b>
                      {deleted}
                    </b>
                    {' '}
                    {deleted > 1 ? 'arquivos não foram encontrados.' : 'arquivo não foi encontrado.'}
                  </span>
                )}
              </div>
            ),
          });
          closeModal();
          await filesAPI.getCurrentCompanyStorage();
          return;
        }
      }
      setAlertConfig({
        type: 'success',
        text: 'Dashboard clonado com sucesso!',
      });
      closeModal();
      await filesAPI.getCurrentCompanyStorage();
    };

    const owner = usersState.users.filter((u) => u.id === dash.owner).reduce((_, cur) => cur, {});
    // console.log(owner);
    setModalConfig({
      title: 'Clonar Dashboard',
      children: <ConfirmCopyToMe dashOwner={owner.name} dashName={dash.name} />,
      yesLabel: 'Sim',
      yesFunc: handle,
      notLabel: 'Não',
      notFunc: () => {},
    });
  };

  /* ========================================================= */

  const handleUseAsTemplate = async (dash) => {
    if (!verifyMemoryAndDashboards()) return;

    const {
      raw, error, msg, reqVariablesDescription,
    } = await gridAPI.genRequiredFromDashboard(dash.id, dash.owner);
    if (error) {
      setAlertConfig({
        type: 'error',
        text: msg,
        child: raw,
      });
      return;
    }
    if (!Object.keys(reqVariablesDescription)?.length) {
      setAlertConfig({
        type: 'error',
        text: 'Não é possível copiar um dashboard sem Kpis',
      });
      return;
    }

    const handleFileChange = async (database) => {
      gridAPI.setLoading(true);
      const { error: er, res, msg: message } = await filesAPI.getFileSample(database, false);
      if (er) {
        setAlertConfig({
          type: 'error',
          text: error.generic,
          child: message,
        });
        gridAPI.setLoading(false);
        return [];
      }
      const tempArray = res.columns.filter((val) => val !== 'index')
        .map((val, i) => ({
          id: val + i,
          label: val,
          value: val,
          type: res.types[val],
        }));
      gridAPI.setLoading(false);
      return tempArray;
    };

    const handleColumnChange = async (database, column, format) => {
      // Recebendo valores unicos
      const { error: columnError, msg: columnMsg, res: columnRes } = await filesAPI.getUnique(
        database,
        column,
        format || '',
      );
      if (columnError) {
        setAlertConfig({ type: 'error', text: columnMsg });
      }
      return columnRes.map((val) => ({ label: val, value: val, id: val }));
    };

    const submit = async ({
      name,
      bindings,
      filters,
      database,
    }) => {
      gridAPI.setLoading(true);
      const result = await templateAPI.applyTemplate(
        `copy_${dash.id}`,
        name,
        database,
        bindings,
        filters,
        dash.group,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: result.raw,
        });
        gridAPI.setLoading(false);
        return;
      }
      await gridAPI.init();
      setAlertConfig({
        type: 'success',
        text: 'Dashboard copiado com sucesso',
      });
      gridAPI.setLoading(false);
      closeModal();
      await filesAPI.init();
    };

    setModalConfig({
      title: 'Copiar Dashboard',
      children: (
        <CopyDashboard
          hide={closeModal}
          variables={reqVariablesDescription}
          // columnData={reqColumns}
          //
          files={filesState.files}
          onSelectFilter={handleColumnChange}
          handleFileChange={handleFileChange}
          submitData={submit}
          tourContext={{ nextStep, tourOpen }}
        />
      ),
    });
  };

  /**
   * @async
   * Makes an exact copy of a dashboard
   * @param {object} dash Dashboard to be copied
   */
  const handleCloneDash = async (dash) => {
    if (!verifyMemoryAndDashboards()) return;

    const result = await gridAPI.cloneDashboard(dash.id);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: result.raw,
      });
      return;
    }
    setAlertConfig({
      type: 'success',
      text: 'Dashboard clonado com sucesso',
    });
    await filesAPI.getCurrentCompanyStorage();
  };

  const handleCloseMessage = async (msgId) => {
    const res = await notificationsAPI.setViewdSystemMessage(msgId);
    if (res.error) {
      setAlertConfig({
        type: 'error',
        text: res.msg,
        child: res.raw,
      });
    }
  };

  const handleImportDashboard = () => {
    const submit = async (file, dashName) => {
      gridAPI.setLoading(true);
      const result = await gridKpiAPI.importDashboard(file, dashName);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        gridAPI.setLoading(false);
        return;
      }
      setAlertConfig({
        type: 'success',
        text: 'Dashboard importado com sucesso',
      });
      gridAPI.setLoading(false);
      closeModal();
      await gridAPI.init();
    };
    setModalConfig({
      title: 'Importar Dashboard',
      children: (
        <ImportDashboard
          uploadFile={submit}
          hide={closeModal}
          tourContext={{ nextStep, tourOpen }}
        />
      ),
    });
  };

  const handleExportDashboard = async (dash) => {
    gridAPI.setLoading(true);
    const result = await gridKpiAPI.exportDashboard(dash.id, dash?.name || 'Dashboard');
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      gridAPI.setLoading(false);
      return;
    }
    setAlertConfig({
      type: 'success',
      text: 'Dashboard exportado com sucesso!',
    });
    gridAPI.setLoading(false);
  };

  const handleMultiSelection = () => setMultiSelection((old) => {
    if (old) {
      handleGeneral('all');
      setSelectedDashs(new Map());
    } else handleGeneral('my');
    return !old;
  });

  const closeMultiSelection = () => {
    handleGeneral('all');
    setSelectedDashs(new Map());
    setMultiSelection(false);
  };

  const toggleSelected = (item) => {
    setSelectedDashs((selected) => {
      const aux = new Map(selected);
      if (!aux.delete(item.id)) aux.set(item.id, item);
      return aux;
    });
  };

  const toggleAllSelected = () => {
    setSelectedDashs((selected) => {
      const allCards = filteredDash?.map((k) => [k.id, k]) ?? [];
      return new Map(selected.size === allCards.length ? [] : allCards);
    });
  };

  const handleToggleTrashDashs = (dashs, toDelete) => {
    const s = dashs.length > 1 ? 's' : '';
    const verifyTrash = async () => {
      const result = await gridAPI.toggleTrashDashboards(dashs, toDelete);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }
      closeModal();
      closeMultiSelection();
      setAlertConfig({
        type: 'success',
        text: `Dashboard${s} ${toDelete ? `movido${s} para a lixeira` : `restaurado${s}`}!`,
      });
    };

    setModalConfig({
      title: toDelete ? `Mover Dashboard${s} para Lixeira` : `Restaurar Dashboard${s}`,
      yesFunc: verifyTrash,
      yesLabel: 'Sim',
      notFunc: closeModal,
      notLabel: 'Cancelar',
      children: (
        <div
          style={{
            padding: '10px 10px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontSize: 16,
            }}
          >
            Tem certeza que deseja
            {toDelete ? ' mover ' : ' restaurar '}
            {`o${s} ${s.length > 0 ? dashs.length : ''} dashboard${s} selecionado${s}`}
            {toDelete ? ' para a lixeira?' : '?'}
          </span>
        </div>
      ),
    });
  };

  const handleTrashSelected = () => {
    handleToggleTrashDashs([...selectedCards.values()], !openTrashCan);
  };

  const handleToggleTrash = (dash) => {
    handleToggleTrashDashs([dash], !openTrashCan);
  };

  const handleMultiDelete = (dashsToDelete = null) => {
    let isEmptyTrash = false;
    const trashedDashs = dashboards?.filter((d) => d.isDeleted);
    // eslint-disable-next-line no-param-reassign
    if (dashsToDelete === null) dashsToDelete = trashedDashs;
    isEmptyTrash = trashedDashs.length === dashsToDelete.length;
    const s = dashsToDelete.length > 1 ? 's' : '';

    const verifyClear = async () => {
      const res = await gridAPI.removeDashboards(dashsToDelete);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: `Houve um erro ao tentar ${isEmptyTrash ? 'esvaziar a lixeira' : `Apagar o${s} dashboard${s}`}!`,
          child: res.raw,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: `${isEmptyTrash ? 'Lixeira esvaziada' : `Dashboard${s} apagado${s}`} com sucesso!`,
      });
      closeModal();
      closeMultiSelection();
      await filesAPI.getCurrentCompanyStorage();
    };

    setModalConfig({
      title: isEmptyTrash ? 'Esvaziar Lixeira' : `Apagar Dashboard${s}`,
      yesFunc: verifyClear,
      yesLabel: 'Apagar',
      notFunc: closeModal,
      notLabel: 'Cancelar',
      children: (
        <div
          style={{
            padding: '10px 10px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontSize: 16,
            }}
          >
            Tem certeza que deseja
            {isEmptyTrash ? ' esvaziar a lixeira? ' : ` apagar este${s} ${s.length > 0 ? dashsToDelete.length : ''} dashboard${s}? `}
            {isEmptyTrash || s.length > 0 ? 'Os dashboards removidos não poderão ser recuperados!' : 'O dashboard removido não poderá ser recuperado!'}
          </span>
        </div>
      ),
    });
  };

  const handleDeleteSelected = () => {
    if (!openTrashCan) return;
    handleMultiDelete([...selectedCards.values()]);
  };

  const handleRemoveDash = (dash) => {
    handleMultiDelete([dash]);
  };

  const handleGetDashsUrls = async () => {
    const dashUrlsInfos = dashWithUser.map((dash) => ({
      ...dash,
      publicUrl: dash?.publicUrl ? `${window.location.origin}${dash.publicUrl}` : '',
      authUrl:
        dash?.id && dash?.owner
          ? `${window.location.origin}/home/${urlHash(`${dash.id}/${dash.owner}`)}`
          : '',
    }));

    const genPublicUrl = async (dash, operation) => {
      const res = await gridAPI.genPublicKpis(
        { ...dash },
        operation,
        null,
        null,
      );
      const isDel = operation === 'delete';
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: `Não foi possível ${isDel ? 'desativar o link público' : 'gerar cópia pública'}. Tente novamente mais tarde`,
        });
      } else {
        setAlertConfig({
          type: 'success',
          text: `Link público ${isDel ? 'desativado' : 'atualizado'} com sucesso.`,
          child: !isDel ? <PublicLinkAlert /> : null,
        });
      }
      return res;
    };

    const getDashInfo = (dash) => {
      const hideDashInfo = () => toggleModal('dash-dependencies');
      setModalConfig({
        title: 'Informações do Dashboard',
        nodeTarget: 'dash-info',
        yesLabel: 'Fechar',
        yesFunc: hideDashInfo,
        children: <DashboardInfo dashboard={dash} />,
      });
    };

    setModalConfig({
      title: 'Gerenciamento de Links dos Dashboards',
      yesLabel: 'Fechar',
      yesFunc: closeModal,
      children: (
        <DashboardsLinks
          dashboards={dashUrlsInfos}
          user={user}
          genPublicUrl={genPublicUrl}
          getDashInfo={getDashInfo}
        />
      ),
    });
  };

  return (
    <>
      {(isLoading/* || filesState.isLoading */) && <Loader />}
      <GridDashboardToolbar
        //
        user={user}
        dashboards={dashWithUser}
        filteredDash={filteredDash}
        saveLayout={saveLayout}
        editPainel={canEditLayout}
        setEditLayout={setEditLayout}
        isLoading={!filesState?.started || filesState?.isLoading}
        // filter
        filterText={filterText}
        setFilterText={setFilterText}
        selectedGeneral={selectedGeneral}
        setSelectedGeneral={handleGeneral}
        selectedGroups={selectedGroups}
        setSelectedGroups={handleGroup}
        openTrashCan={openTrashCan}
        setOpenTrashCan={handleTrashCan}
        trashCount={countTrashCan}
        clearTrashCan={handleMultiDelete}
        //
        multiSelection={multiSelection}
        selectedCards={selectedCards}
        toggleAllSelected={toggleAllSelected}
        setMultiSelection={handleMultiSelection}
        trashSelected={handleTrashSelected}
        deleteSelected={handleDeleteSelected}
        //
        groups={groups}
        handleCreateModal={handleCreateModal}
        importDashboard={handleImportDashboard}
        handleGetDashsUrls={handleGetDashsUrls}
        tourContext={{ tourOpen, nextStep }}
      />
      <DashboardCore
        _currentBreakpoint={_currentBreakpoint}
        _layout={_layout}
        _setCurrentBreakPoint={_setCurrentBreakPoint}
        _setLayout={_setLayout}
        canEditLayout={canEditLayout}
        dashboardLayout={dashboardLayout}
        filesState={filesState}
        filteredDash={filteredDash}
        filterText={filterText}
        handleBookMarkDash={handleBookMarkDash}
        handleCloneDash={handleCloneDash}
        handleCloseMessage={handleCloseMessage}
        handleCopyDashboard={handleCopyDashboard}
        handleCreateModal={handleCreateModal}
        handleEditModal={handleEditModal}
        handleExportDashboard={handleExportDashboard}
        handleRemoveDash={handleRemoveDash}
        handleSelfRemove={handleSelfRemove}
        handleShareDash={handleShareDash}
        handleToggleTrash={handleToggleTrash}
        handleTrashCan={handleTrashCan}
        handleUseAsTemplate={handleUseAsTemplate}
        isLoading={isLoading}
        nextStep={nextStep}
        openTrashCan={openTrashCan}
        refreshTour={refreshTour}
        selectedGeneral={selectedGeneral}
        selectedGroups={selectedGroups}
        sysMsgState={sysMsgState}
        tourOpen={tourOpen}
        user={user}
        multiSelection={multiSelection}
        selectedCards={selectedCards}
        toggleSelected={toggleSelected}
      />
    </>
  );
}
